import React, { useCallback, useEffect, useState } from 'react';
import propTypes from 'prop-types';

import { Box, Stack, useBreakpointValue, useToast } from '@chakra-ui/react';
import { Card } from '@outdoorly/card';
import { Button } from '@outdoorly/button';
import { Modal } from '@outdoorly/modal';
import { Text } from '@outdoorly/text';
import { Field, TextInput, TextareaInput } from '@outdoorly/input';
import { RadioGroup, Radio } from '@outdoorly/radio';
import { RatingInput } from '@outdoorly/star-rating';
import NextImage from 'next/image';
import { gql, useMutation } from '@apollo/client';

import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { ImageUpload } from './image-upload';
import { dirtyValues, getImageLoader } from '@outdoorly/util';

const CREATE_REVIEW = gql`
  mutation CreateReview($input: ReviewCreateInput!) {
    reviewCreate(input: $input) {
      review {
        id
      }
      reviewErrors {
        code
      }
    }
  }
`;

const ReviewFormProductPreview = ({ thumbnailUrl, brandName, productName }) => {
  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <Card backgroundColor="scheme.bgSecondary" padding={0} overflow="hidden">
        <Box boxSize={14}>
          {thumbnailUrl && (
            <NextImage
              loader={getImageLoader(thumbnailUrl)}
              src={thumbnailUrl}
              width={56}
              height={56}
            />
          )}
        </Box>
      </Card>
      <Box flexGrow={1}>
        <Stack spacing={3}>
          <Text color="scheme.textSecondary" size="sm">
            {brandName}
          </Text>
          <Text fontWeight="500">{productName}</Text>
        </Stack>
      </Box>
    </Stack>
  );
};

ReviewFormProductPreview.propTypes = {
  brandName: propTypes.string,
  productName: propTypes.string,
  thumbnailUrl: propTypes.string,
};

const ReviewForm = () => {
  const { control, register } = useFormContext();
  const MAX_REVIEW_LENGTH = 600;
  const [characterCount, setCharacterCount] = useState(MAX_REVIEW_LENGTH);
  return (
    <Stack spacing="xl">
      <Field label="Rating" isRequired>
        <Controller
          name="rating"
          control={control}
          as={RatingInput}
          rules={{
            required: { value: true, message: 'A rating is required' },
          }}
        />
      </Field>
      <Field label="Review headline" isRequired>
        <TextInput
          name="headline"
          placeholder="I would buy this over and over!"
          ref={register({
            required: { value: true, message: 'A headline is required' },
          })}
        />
      </Field>
      <Field label="Review" isRequired>
        <TextareaInput
          maxLength={600}
          name="reviewText"
          placeholder="What was great about it? What needed some improvement?"
          ref={register({
            required: { value: true, message: 'A review is required' },
          })}
          onChange={(e) =>
            setCharacterCount(MAX_REVIEW_LENGTH - e.target.value?.length || 0)
          }
        />
      </Field>
      <Text
        size="sm"
        color="scheme.textSecondary"
      >{`${characterCount} / ${MAX_REVIEW_LENGTH}`}</Text>
      <Field label="Add a photo">
        <Controller
          control={control}
          as={ImageUpload}
          maxFiles={1}
          name="images"
        />
      </Field>
      <Field label="Bottom line" isRequired>
        <Controller
          as={RadioGroup}
          name="recommended"
          defaultValue="yes"
          ref={register}
        >
          <Stack spacing={3}>
            <Radio value="yes" isFullWidth>
              <Text>Yes, I would recommend this to a friend.</Text>
            </Radio>
            <Radio value="no" isFullWidth>
              <Text>No, I would not recommend this to a friend.</Text>
            </Radio>
          </Stack>
        </Controller>
      </Field>
    </Stack>
  );
};

const ReviewFooter = ({ onClose, onSubmit }) => {
  const breakpoint = useBreakpointValue({ base: 'mobile', md: 'desktop' });

  const { formState } = useFormContext();
  const { isSubmitting, isValid, isDirty } = formState;

  return (
    <Box
      width={breakpoint === 'mobile' ? '100%' : null}
      display={breakpoint === 'desktop' ? 'flex' : null}
      align="right"
      flexDirection={breakpoint === 'desktop' ? 'row-reverse' : null}
    >
      <Button
        width="100%"
        colorScheme="outline"
        fontWeight="500"
        size="lg"
        isLoading={isSubmitting}
        isDisabled={!isValid || !isDirty}
        onClick={onSubmit}
        mb={breakpoint === 'mobile' ? 3 : null}
      >
        Save review
      </Button>
      <Button
        width="100%"
        colorScheme="danger"
        fontWeight="500"
        size="lg"
        isLoading={false}
        onClick={onClose}
        variant="solid"
        mr={breakpoint === 'desktop' ? 3 : null}
      >
        Cancel
      </Button>
    </Box>
  );
};

ReviewFooter.propTypes = {
  onClose: propTypes.func,
  onSubmit: propTypes.func,
};

export const AddReview = ({ product, isOpen, onClose }) => {
  const [createReview] = useMutation(CREATE_REVIEW);
  const toast = useToast();

  const formContext = useForm({ mode: 'onChange' });

  const { handleSubmit } = formContext;

  const onSubmit = useCallback(
    async (values) => {
      const { data } = await createReview({
        variables: {
          input: {
            headline: values.headline,
            reviewText: values.reviewText,
            product: product.id,
            starRating: values.rating,
            recommend: values.recommended === 'yes',
            image: values.thumbnail,
          },
        },
      });

      if (data?.reviewCreate.reviewErrors?.length) {
        toast({
          status: 'error',
          title: 'Something went wrong...',
          message:
            "We weren't able to save your review. Please try again later.",
        });
        return false;
      }

      onClose();
      toast({
        status: 'success',
        title: 'Thanks for the review!',
        description:
          "We appreciate you taking the time to leave a review. You're a star!",
      });
    },
    [toast, createReview, product, onClose]
  );

  return (
    <FormProvider {...formContext}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Write a review"
        scrollBehavior="inside"
        size="lg"
        footer={
          <ReviewFooter onClose={onClose} onSubmit={handleSubmit(onSubmit)} />
        }
      >
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={5}>
            <ReviewFormProductPreview
              productName={product.name}
              brandName={product.brand.name}
              thumbnailUrl={product.thumbnail?.url}
            />
            <ReviewForm />
          </Stack>
        </Box>
      </Modal>
    </FormProvider>
  );
};

AddReview.propTypes = {
  isOpen: propTypes.bool,
  onClose: propTypes.func,
  product: propTypes.shape({
    id: propTypes.string,
    thumbnail: propTypes.shape({
      url: propTypes.string,
    }),
    brand: propTypes.shape({
      name: propTypes.string.isRequired,
    }),
    name: propTypes.string.isRequired,
  }),
};

const UPDATE_REVIEW = gql`
  mutation UpdateReview($id: ID!, $input: ReviewUpdateInput!) {
    reviewUpdate(id: $id, input: $input) {
      review {
        id
        headline
        reviewText
        starRating
        recommend
        image {
          url
        }
      }
      reviewErrors {
        field
        message
      }
    }
  }
`;

export const EditReview = ({ review, product, isOpen, onClose }) => {
  const [updateReview] = useMutation(UPDATE_REVIEW);
  const toast = useToast();

  const formContext = useForm({ mode: 'onChange' });

  const { handleSubmit, formState, reset } = formContext;
  const { dirtyFields } = formState;

  console.log(dirtyFields);

  useEffect(() => {
    if (isOpen) {
      reset({
        headline: review.headline,
        reviewText: review.reviewText,
        rating: review.starRating,
        recommended: review.recommend ? 'yes' : 'no',
        images: review.image
          ? [
              {
                preview: review.image.url,
              },
            ]
          : [],
      });
    }
  }, [isOpen, reset, review]);

  const onSubmit = useCallback(
    async (values) => {
      const dirty = dirtyValues(dirtyFields, values);

      if (Object.hasOwnProperty.call(dirty, 'recommended')) {
        dirty.recommend = values.recommended === 'yes';
        delete dirty.recommended;
      }

      if (dirty.images) {
        dirty.image = dirty.images?.[0] ?? null;
        delete dirty.images;
      }

      if (dirty.rating) {
        dirty.starRating = dirty.rating;
        delete dirty.rating;
      }

      const { data } = await updateReview({
        variables: {
          id: review.id,
          input: {
            ...dirty,
          },
        },
      });

      if (data?.reviewUpdate.reviewErrors?.length) {
        toast({
          status: 'error',
          title: 'Something went wrong...',
          message:
            "We weren't able to save your review. Please try again later.",
        });
        return false;
      }

      onClose();
      toast({
        status: 'success',
        title: 'Thanks for the review!',
        description:
          "We appreciate you taking the time to leave a review. You're a star!",
      });
    },
    [dirtyFields, updateReview, review, onClose, toast]
  );

  return (
    <FormProvider {...formContext}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Write a review"
        scrollBehavior="inside"
        size="lg"
        footer={
          <ReviewFooter onClose={onClose} onSubmit={handleSubmit(onSubmit)} />
        }
      >
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={5}>
            <ReviewFormProductPreview
              productName={product.name}
              brandName={product.brand.name}
              thumbnailUrl={product.thumbnail?.url}
            />
            <ReviewForm />
          </Stack>
        </Box>
      </Modal>
    </FormProvider>
  );
};

EditReview.propTypes = {
  isOpen: propTypes.bool,
  onClose: propTypes.func,
  product: propTypes.shape({
    brand: propTypes.shape({
      name: propTypes.string.isRequired,
    }),
    id: propTypes.string,
    name: propTypes.string.isRequired,
    thumbnail: propTypes.shape({
      url: propTypes.string,
    }),
  }),
  review: propTypes.shape({
    id: propTypes.string,
    headline: propTypes.string,
    image: propTypes.shape({
      url: propTypes.string,
    }),
    recommend: propTypes.bool,
    reviewText: propTypes.string,
    starRating: propTypes.number,
  }),
};
